/** Maximum decoded archive size accepted by the Nexus submission boundary. */
export const GOOGLE_NEXUS_MAX_ARCHIVE_BYTES = 50 * 1024 * 1024;

export type GoogleNexusIntegrationType = 'sxs' | 'rl';
export type GoogleNexusApiEnvironment = 'production' | 'staging';
export type GoogleNexusValidationFormat = 'dockerWorlds' | 'harbor';

export interface GoogleNexusUploadStats {
  total: number;
  active: number;
  completed: number;
  failed: number;
}

export interface GoogleNexusProject {
  deliveryName?: string | null;
  id: string;
  requestName: string | null;
  displayName: string;
  integrationType: GoogleNexusIntegrationType;
  apiEnvironment: GoogleNexusApiEnvironment;
  deliveryBugId: string | null;
  archivedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  remoteState: string | null;
  remoteDisplayName: string | null;
  requestTemplate: string | null;
  remoteTaskCount: number | null;
  remoteTaskCountExact: boolean | null;
  remoteTaskStateCounts?: Record<string, number> | null;
  lastSyncedAt: string | null;
  lastConnectionTest?: GoogleNexusConnectionResult | null;
  lastSyncError?: unknown;
  uploadStats: GoogleNexusUploadStats;
  /** Newest cumulative delivery collection across this project's uploads. */
  latestDeliveryCollection?: { latestDeliveryCollectionId: string; remoteProjectId: string | null } | null;
}

export interface GoogleNexusUpload {
  id: string;
  nexusProjectId: string;
  requestName: string | null;
  apiEnvironment?: GoogleNexusApiEnvironment;
  displayName: string;
  status: string;
  validationFormat?: GoogleNexusValidationFormat;
  testRun: boolean;
  archiveName: string;
  archiveSizeBytes: number;
  archiveSha256?: string;
  remoteProjectId: string | null;
  /**
   * Cumulative delivery collection (`<vendor>_<delivery>@delivery_upto_<P-id>`),
   * set once ingestion completes. Share this with researchers, not P- IDs.
   */
  latestDeliveryCollectionId?: string | null;
  createdAt: string | null;
  submittedAt?: string | null;
  completedAt?: string | null;
  lastPolledAt?: string | null;
  requestSummary?: unknown;
  remoteResponse?: unknown;
  error?: unknown;
  lastPollError?: unknown;
}

export interface NexusValidationResultSummary {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  errored: number;
  statusMessage: string | null;
}

/**
 * Dashboard row. The raw request/response payloads stay on the server; fetch
 * the full record with `fetchUpload` when the detail view needs them.
 */
export type GoogleNexusUploadListItem = Omit<
  GoogleNexusUpload,
  'requestSummary' | 'remoteResponse' | 'error'
> & {
  resultSummary: NexusValidationResultSummary | null;
};

export interface GoogleNexusDashboard {
  authConfigured: boolean;
  projects: GoogleNexusProject[];
  /** Most recent uploads only; `stats` counts every upload. */
  uploads: GoogleNexusUploadListItem[];
  stats: {
    projectCount: number;
    uploadCount: number;
    activeUploads: number;
    completedUploads: number;
    failedUploads: number;
  };
  loadedAt: string;
}

export interface GoogleNexusProjectInput {
  id?: string;
  requestName: string;
  displayName: string;
  integrationType: GoogleNexusIntegrationType;
  apiEnvironment: GoogleNexusApiEnvironment;
  deliveryBugId?: string;
}

export interface GoogleNexusConnectionResult {
  ok: boolean;
  latencyMs: number;
  httpStatus?: number;
  requestId?: string | null;
  checkedAt: string;
  request?: unknown;
  error?: unknown;
}

export interface GoogleNexusSyncResult {
  ok: boolean;
  latencyMs: number;
  request?: unknown;
  template?: unknown;
  taskStats?: {
    count: number;
    exact: boolean;
    pageCount: number;
    stateCounts: Record<string, number>;
    sampleTaskNames: string[];
  } | null;
  error?: unknown;
}

export interface StagedGoogleNexusArchive {
  uploadId: string;
  storagePath: string;
  archiveName: string;
  sizeBytes: number;
}

export interface GoogleNexusValidationInput {
  projectId: string;
  uploadId: string;
  storagePath: string;
  archiveName: string;
  validationFormat: GoogleNexusValidationFormat;
  containerImageUri: string;
  worldName: string;
  taskId: string;
  batchName: string;
  deliveryBugId: string;
  validatorNames: string[];
  maxValidationTasks: number | null;
  mcpStartupWaitTimeSeconds: number | null;
  testRun: boolean;
}

export interface GoogleNexusValidationResult {
  ok: boolean;
  uploadId: string;
  status: string;
  remoteProjectId?: string | null;
  response?: unknown;
  error?: unknown;
  requestSummary?: unknown;
}

export interface GoogleNexusRefreshResult {
  ok: boolean;
  uploadId: string;
  status?: string;
  response?: unknown;
  error?: unknown;
}

export function newGoogleNexusUploadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `nexus-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Outcome of a task in the per-task delivery ledger (functions/domains/nexus/ledger.js). */
export type NexusLedgerOutcome = 'submitted' | 'ingested' | 'rejected' | 'unknown';

export interface NexusLedgerAttempt {
  deliveryId: string;
  outcome: NexusLedgerOutcome;
  sourceHash: string | null;
  remoteProjectId: string | null;
  submittedAt: string | null;
}

/** Current state of one delivered Nexus task ID. */
export interface NexusDeliveredTask {
  taskId: string;
  versionedTaskId: string | null;
  version: string | null;
  status: NexusLedgerOutcome | null;
  taskState: string | null;
  deliveryBugId: string | null;
  deliveryName: string | null;
  testRun: boolean | null;
  sourceTaskId: string | null;
  synthPipelineId: string | null;
  sourceKind: 'local' | 'gcs' | 'coordinatorBatch' | 'unknown' | null;
  sourceUri: string | null;
  sourcePath: string | null;
  sourceHash: string | null;
  currentProjectId: string | null;
  currentDeliveryId: string | null;
  projectIds: string[] | null;
  deliveryIds: string[] | null;
  firstSubmittedAt: string | null;
  currentSubmittedAt: string | null;
  lastAttempt: NexusLedgerAttempt | null;
}

export interface NexusDeliveredTasksQuery {
  /**
   * Task ID or source task ID prefix, an all-digit bug ID, or a `P-…` Nexus
   * project. A versioned `id@version` searches its task ID.
   */
  query?: string;
  limit?: number;
}

/** Which searches found the returned tasks. */
export type NexusDeliveredTaskMatch = 'taskId' | 'sourceTaskId' | 'deliveryBugId' | 'projectId';

/** The project name and owner shown under a bug ID. */
export interface NexusBugLabel {
  projectName: string;
  projectOwner: string;
}

export interface NexusDeliveredTasksResult {
  query: string;
  matchedOn: NexusDeliveredTaskMatch[];
  tasks: NexusDeliveredTask[];
  /** Saved details for the bugs among `tasks`, keyed by bug ID. */
  bugs?: Record<string, NexusBugLabel>;
  hasMore: boolean;
}

export type NexusOutcomeCounts = Record<NexusLedgerOutcome, number>;

/** One bug on the Delivered Tasks overview. Task counts are distinct delivered task IDs. */
export interface NexusBugSummary extends NexusBugLabel {
  deliveryBugId: string;
  deliveryNames: string[];
  /** Ledger deliveries with a Nexus project ID. */
  projectCount: number;
  /** Ledger deliveries whose POST outcome was unknown, so they have no project. */
  attemptCount: number;
  taskCount: number;
  counts: NexusOutcomeCounts;
  lastDeliveredAt: string | null;
}

export interface NexusDeliveryOverview {
  bugs: NexusBugSummary[];
}

/** One ledger delivery: a Nexus project, or an attempt with an unknown outcome. */
export interface NexusLedgerDelivery {
  deliveryId: string;
  remoteProjectId: string | null;
  deliveryBugId: string | null;
  deliveryName: string | null;
  deliveryType: string | null;
  changeReason: string | null;
  testRun: boolean | null;
  writer: string | null;
  originalWriter: string | null;
  kind: string | null;
  receiptStatus: string | null;
  projectState: string | null;
  outcomeCounts: Partial<NexusOutcomeCounts> | null;
  taskCount: number | null;
  source: { kind: string | null; uri: string | null; batchId?: string | null } | null;
  submittedAt: string | null;
  lastPolledAt: string | null;
  lastSyncError: string | null;
}

export interface NexusBugDetail {
  deliveryBugId: string;
  bug: NexusBugMetadata;
  deliveryNames: string[];
  projects: NexusLedgerDelivery[];
  taskCount: number;
  counts: NexusOutcomeCounts;
  /** Task IDs Nexus accepted for this bug, for one-click copy. */
  deliveredTaskIds: string[];
}

/** One task as sent in one project, with Nexus's result for it there. */
export interface NexusDeliveryItem {
  taskId: string;
  versionedTaskId: string | null;
  version: string | null;
  outcome: NexusLedgerOutcome | null;
  taskState: string | null;
  validationStates: Record<string, number> | null;
  resultComment: string | null;
  sourceTaskId: string | null;
  synthPipelineId: string | null;
  sourceKind: NexusDeliveredTask['sourceKind'];
  sourceUri: string | null;
  sourcePath: string | null;
  sourceHash: string | null;
  previousVersionedTaskId: string | null;
  taskArchiveUri: string | null;
  submittedAt: string | null;
}

export interface NexusProjectDetail {
  project: NexusLedgerDelivery | null;
  tasks: NexusDeliveryItem[];
  /** Task IDs Nexus accepted in this project, for one-click copy. */
  deliveredTaskIds: string[];
}

export type NexusExportScope = 'all' | 'bug' | 'project';

export interface NexusCsvExport {
  filename: string;
  rowCount: number;
  csv: string;
}

/** Admin-authored details for a delivery bug (`nexusBugs/{deliveryBugId}`). */
export interface NexusBugMetadata extends NexusBugLabel {
  deliveryBugId: string;
  description: string;
  updatedAt: string | null;
  updatedBy: string | null;
  /** The last cost (`reportMetrics`) and estimate (`reportEstimates`) sent to Google. */
  lastCostReport?: NexusBugCostReport | null;
  lastEstimateReport?: NexusBugEstimateReport | null;
}

/** `cost` is sent through Google's reportMetrics endpoint, `estimate` through reportEstimates. */
export type NexusBugReportKind = 'cost' | 'estimate';

/** Values Google accepts; checked by functions/domains/nexus/bugReports.js. */
export const NEXUS_RATER_TIERS = ['Tier 1', 'Tier 2', 'Tier 3', 'Generalist'] as const;
export const NEXUS_EXPENSE_TYPES = ['Pay Per Unit (PPU)', 'Time and Materials (T&M)'] as const;
export type NexusRaterTier = (typeof NEXUS_RATER_TIERS)[number];
export type NexusExpenseType = (typeof NEXUS_EXPENSE_TYPES)[number];

/** Rater cost for one tier and locale. Money is USD cents; the rate is per hour (T&M) or per task (PPU). */
export interface NexusRaterCost {
  raterTier: NexusRaterTier;
  locale: string;
  ahtSeconds: number;
  totalMinutes: number;
  /** Items or tasks, including all replications. */
  totalVolume: number;
  costRateCents: number;
  costCents: number;
  expenseType: NexusExpenseType;
}

/** A cost other than raters, such as tokens. */
export interface NexusAuxiliaryCost {
  costType: string;
  description: string;
  costCents: number;
}

/** One period's actual cost. Google needs at least one rater or auxiliary line. */
export interface NexusBugCostInput {
  /** ISO time the period's costs are reported for. */
  reportTime: string;
  /** Full (non-discounted) total for the period. */
  costCents: number;
  discountedPriceCents: number | null;
  raterCosts: NexusRaterCost[];
  auxiliaryCosts: NexusAuxiliaryCost[];
}

/** The study's expected total cost and, optionally, its average handling time. */
export interface NexusBugEstimateInput {
  reportTime: string;
  costCents: number;
  ahtSeconds: number | null;
}

interface NexusBugReportReceipt {
  apiEnvironment: 'production' | 'staging';
  requestId: string | null;
  reportedAt: string;
  reportedBy: string;
}

export type NexusBugCostReport = NexusBugCostInput & NexusBugReportReceipt;
export type NexusBugEstimateReport = NexusBugEstimateInput & NexusBugReportReceipt;

export type NexusBugReportResult =
  | { deliveryBugId: string; kind: 'cost'; report: NexusBugCostReport }
  | { deliveryBugId: string; kind: 'estimate'; report: NexusBugEstimateReport };

export type NexusBugInput = Pick<NexusBugMetadata, 'projectName' | 'projectOwner' | 'description'>;

/** Limits enforced by functions/domains/nexus/bugMetadata.js. */
export const NEXUS_BUG_LIMITS: Record<keyof NexusBugInput, number> = {
  projectName: 120,
  projectOwner: 120,
  description: 5000,
};

/** One row on the Bugs tab: saved details plus delivery totals. */
export interface NexusBugListRow extends NexusBugMetadata {
  deliveryNames: string[];
  projectCount: number;
  taskCount: number;
  lastDeliveredAt: string | null;
}

export interface NexusBugList {
  bugs: NexusBugListRow[];
}

export interface NexusBugEditor {
  bug: NexusBugMetadata;
  deliveryNames: string[];
  projectCount: number;
}
